import { Observable, Subject } from 'rxjs';
import { BaseMessage } from '../messages';
import { Injectable, OnDestroy } from '@angular/core';
import { CallbackType } from '../mcrc-api';

/** This service creates and maintains RxJS observables (Subject) to replace traditional callbacks.
 *  Observables emit BaseRequestMessage-derived message objects typically containing a parsed response 
 *  data. Notably, these observables trigger synchronously in order of creation; they should match the 
 *  order in which replies are received, assuming that the replies are processed synchronously when
 *  the order is important. In the case of multiple subscribers RxJS notifies subscribers in 
 *  a synchronous sequence.
 * 
 * The caller service should handle errors and unsubscribe from completes to prevent memory leaks. 
 * complete() is called both from the processReply() when a reply message is received, and from
 * cancel() in case of errors. There might be instances when a message reply never arrives (e.g. when
 * the websocket is closed). To prevent memory leaks the service will 
 * 
 */

@Injectable({ providedIn: "root" })
export class RequestReplyService implements OnDestroy {
  private requestObservables: Map<number, Subject<BaseMessage>>;
  public callbacks: Map<number, CallbackType>;
  public fallbacks: Map<number, CallbackType>;

  constructor() {
    this.requestObservables = new Map<number, Subject<BaseMessage>>();
    this.callbacks = new Map<number, CallbackType>();
    this.fallbacks = new Map<number, CallbackType>();
  }

  ngOnDestroy(): void {
    this.dispose();
  }

  /** Register a message with an observer */
  public registerMessage(reqId: number): Observable<BaseMessage> {
    const requestSubject = new Subject<BaseMessage>();
    this.requestObservables.set(reqId, requestSubject);
    return requestSubject.asObservable();
  }

  /** Process a reply and notify the subscribers, then remove the request observable
   * When a request registers a callback rather than an observable, i.e. when requested
   * via *sendCallbackRequest*, the additional params can be passed to the callback
   */
  public processReply(reply: BaseMessage, ...args: any): boolean {
    const reqId = reply.reqId;
    const requestSubject = this.requestObservables.get(reqId);

    if (requestSubject) {
      if (!!reply.errorCode) {
        requestSubject.error(reply.errorCode)
      } else {
        requestSubject.next(reply);
      }
      requestSubject.complete();
      this.requestObservables.delete(reqId);
      return true;
    }
    // When Subject not found, it is either a callback reply or a non monitored request
    return this.processCallback.apply(this, [reply, ...args]);
  }

  /** cancel the subscription with or /o an error code */
  public cancel(reqId: number, errorCode: number = 0) {
    const requestSubject = this.requestObservables.get(reqId);
    if (requestSubject) {
      if (!!errorCode) {
        requestSubject.error(errorCode);
      }
      requestSubject.complete();
      this.requestObservables.delete(reqId);
    } else {
      this.cancelCallback(reqId, errorCode);
    }
  }

  private processCallback(reply: BaseMessage, ...args: any): boolean {
    const reqId = reply.reqId;
    const callback = this.callbacks.get(reqId);

    if (callback) {
      const fallback = this.fallbacks.get(reqId);
      if (!!reply.errorCode) {
        if (fallback) {
          fallback(reply.errorCode)
        }
      } else {
        callback.apply(this, args);
      }
      this.callbacks.delete(reqId);
      this.fallbacks.delete(reqId);
      return true;
    }
    return false;
  }

  /** cancel the subscription with or /o an error code */
  public cancelCallback(reqId: number, errorCode: number = 0) {
    if (!!errorCode) {
      const fallback = this.fallbacks.get(reqId);
      if (fallback) {
        fallback(errorCode);
      }
    }
    this.callbacks.delete(reqId);
    this.fallbacks.delete(reqId);
  }

  public dispose() {
    this.requestObservables.forEach((value: Subject<BaseMessage>, key: number) => {
      value.complete();
    });
    this.callbacks.clear();
    this.fallbacks.clear();
  }
}